import { z } from 'zod';
import { BADGE_PRINTER_STATUS_CODES } from './badge-printer.js';
import { pageQuerySchema } from './http.js';
import { multiLanguageContentSchema } from './i18n.js';
/** PRT-03's lifecycle. PENDING is reachable two ways: a reprint staff asked for that no kiosk
 *  has executed yet (PRT-08), and nothing else. A kiosk NEVER reports PENDING. */
export const BADGE_PRINT_RECORD_STATUSES = ['PENDING', 'PRINTED', 'FAILED'];
export const badgePrintRecordStatusSchema = z.enum(BADGE_PRINT_RECORD_STATUSES);
/** Who caused the attempt. CHECK_IN has no human actor; REPRINT always does (PRT-06). */
export const BADGE_PRINT_TRIGGERS = ['CHECK_IN', 'REPRINT'];
export const badgePrintTriggerSchema = z.enum(BADGE_PRINT_TRIGGERS);
/**
 * The reasons the kiosk produces today, copied verbatim from
 * `apps/kiosk/lib/core/printing/badge-transport.dart`'s `kBadgePrintFailureReasons`.
 *
 * A LABEL MAP, NOT A ZOD ENUM — read the note in §Edge cases. `reportedBadgePrintSchema`
 * validates `failureReason` as a bounded string, and this array exists so a console can
 * translate the ones it knows and fall back for the ones it does not.
 */
export const BADGE_PRINT_FAILURE_REASONS = [
    'unsupported_platform',
    'unsupported_encoding',
    'unreachable',
    'timed_out',
    'write_failed',
    'cancelled',
    'no_printer',
    'render_failed',
    'encode_failed',
    'media_mismatch',
    'printer_error',
];
export const BADGE_PRINT_FAILURE_REASON_MAX_LENGTH = 64;
/** One POST carries at most this many reported attempts. A kiosk with a longer backlog drains
 *  it over several syncs; an unbounded array is an unbounded transaction. */
export const MAX_BADGE_PRINT_RECORDS_PER_SYNC = 50;
/** At most this many pending reprints come back in one sync response. Each carries a photo
 *  data URI, so the cap is a payload bound, not a fairness rule; the server returns the
 *  OLDEST first so nothing starves. */
export const MAX_PENDING_REPRINTS_PER_SYNC = 5;
/** One attempt, as the kiosk reports it. The kiosk never reports PENDING and never names a
 *  printer, a location or a tenant: all three come from the verified Device row (TRN-02, S1). */
export const reportedBadgePrintSchema = z
    .object({
    /** Client-generated on the device BEFORE the label is sent (PRT-09). Reporting the same
     *  attemptId twice produces exactly one server row. */
    attemptId: z.string().min(1).max(64),
    visitId: z.string().min(1),
    status: z.enum(['PRINTED', 'FAILED']),
    /** Null when `status` is PRINTED; a bounded string when FAILED. */
    failureReason: z.string().min(1).max(BADGE_PRINT_FAILURE_REASON_MAX_LENGTH).nullable(),
    /** The decoded printer condition, when the reason was `printer_error`. Null otherwise. */
    failureCondition: z.string().min(1).max(BADGE_PRINT_FAILURE_REASON_MAX_LENGTH).nullable(),
    /** When the device attempted it — ISO 8601. The ONLY witness of when an offline print
     *  happened, which is why the activity list orders by it and not by row creation. */
    attemptedAt: z.string().datetime(),
    /**
     * What the kiosk's pre-print `ESC i S` check observed for THIS attempt. `UNAVAILABLE`
     * means the printer answered nothing to the status request — the label still went out,
     * but its media was never verified against the template. `null` means no check was even
     * attempted: the print never reached the printer at all (no printer configured for this
     * device, an unsupported platform, or a render/encode failure upstream of the transport).
     */
    printerStatus: z.enum(BADGE_PRINTER_STATUS_CODES).nullable(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.status === 'PRINTED' && value.failureReason !== null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'PRINTED_WITH_FAILURE_REASON', path: ['failureReason'] });
    }
    if (value.status === 'FAILED' && value.failureReason === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'FAILED_WITHOUT_FAILURE_REASON', path: ['failureReason'] });
    }
    if (value.failureCondition !== null && value.failureReason !== 'printer_error') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'CONDITION_WITHOUT_PRINTER_ERROR', path: ['failureCondition'] });
    }
    // `media_mismatch` and `printer_error` can only be produced BY the verifier — the first
    // is a comparison against the printer's status block, the second is decoded from it. When
    // `printerStatus` is UNAVAILABLE the verifier never ran, so neither reason is reachable;
    // a kiosk reporting both is lying about one of them.
    if (value.printerStatus === 'UNAVAILABLE' && (value.failureReason === 'media_mismatch' || value.failureReason === 'printer_error')) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'UNAVAILABLE_STATUS_WITH_VERIFIER_FAILURE_REASON', path: ['printerStatus'] });
    }
});
/** The body of `POST /api/device/v1/visitor/badge-print-records`. An EMPTY array is legal and
 *  is the ordinary case: the same call is how a kiosk collects its pending reprints. */
export const syncBadgePrintRecordsSchema = z
    .object({ records: z.array(reportedBadgePrintSchema).max(MAX_BADGE_PRINT_RECORDS_PER_SYNC) })
    .strict();
/**
 * A reprint staff asked for, handed to the kiosk that will execute it (PRT-06, PRT-08).
 *
 * CARRIES VISIT DATA, NEVER A BITMAP AND NEVER A TEMPLATE. PRT-06a: a reprint RE-RENDERS
 * against the template in force now, from the display list the kiosk already caches. Every
 * field here is a value the kiosk cannot know about an old visit — nothing more.
 *
 * S7 allow-list: no `printerId`, no `tenantId`, no `locationId`, no `hostPersonId`, no
 * visitor email, no `actorUserId`.
 */
export const pendingBadgeReprintDTOSchema = z
    .object({
    /** SERVER-generated for a reprint, and stable across polls: the kiosk reports back with
     *  this exact value, which is what turns the PENDING row terminal. */
    attemptId: z.string().min(1).max(64),
    visitId: z.string().min(1),
    /** Selects the badge down the BDG-01 ladder in the kiosk's cached configuration. */
    visitorTypeId: z.string().min(1).nullable(),
    visitorName: z.string().min(1),
    visitorCompany: z.string().nullable(),
    hostName: z.string().nullable(),
    /** Unresolved, exactly as `VisitDTO.visitorTypeLabel` carries it — the kiosk resolves it
     *  against its own locale, so a reprint reads the same way the check-in badge did. */
    visitorTypeLabel: multiLanguageContentSchema.nullable(),
    locationName: z.string().nullable(),
    /** ISO 8601. The kiosk formats it and derives the expiry from its cached
     *  `validityMinutes`, exactly as `buildBadgeVisitData` already does at check-in. */
    checkedInAt: z.string().datetime(),
    /** `data:image/...;base64,...`, or null when the visit has no photo or retention already
     *  removed it. Produced by the same `readBadgePhotoDataUri` the preview route uses. */
    photoDataUri: z.string().min(1).nullable(),
})
    .strict();
/** The answer to one sync: what was taken, and what to do next. */
export const badgePrintSyncResultDTOSchema = z
    .object({
    /** How many of the posted records the server now holds — including ones it already held
     *  (PRT-09: re-reporting is a no-op, not an error). */
    accepted: z.number().int().nonnegative(),
    pending: z.array(pendingBadgeReprintDTOSchema).max(MAX_PENDING_REPRINTS_PER_SYNC),
})
    .strict();
/** One row of the print activity list (PRT-10). */
export const badgePrintRecordDTOSchema = z
    .object({
    id: z.string().min(1),
    visitId: z.string().min(1),
    /** The visitor the label was for — the activity list is unreadable without it, and the
     *  reader already holds `visitor.visit.read`. */
    visitorName: z.string().min(1),
    status: badgePrintRecordStatusSchema,
    trigger: badgePrintTriggerSchema,
    /** The kiosk that printed (or will print) it, by operator-facing name. NULL when that
     *  device has since been deleted. NEVER a device id, a token or a host. */
    deviceName: z.string().nullable(),
    /** The printer's operator-facing name. NULL when unresolved or since deleted. NEVER the
     *  printer's host or port — infrastructure detail, S7. */
    printerName: z.string().nullable(),
    /** `null` for a CHECK_IN print: nobody asked for it. */
    actorUserId: z.string().nullable(),
    failureReason: z.string().nullable(),
    failureCondition: z.string().nullable(),
    attemptedAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
    /** What the kiosk's pre-print check observed for this attempt (see
     *  `reportedBadgePrintSchema.printerStatus`). Lets the activity list tell "printed and
     *  verified" apart from "printed, but media was never checked" instead of both reading
     *  as a plain PRINTED row. */
    printerStatus: z.enum(BADGE_PRINTER_STATUS_CODES).nullable(),
})
    .strict();
/** `GET /api/web/visitor/badge-print-records`. `locationId` is REQUIRED — print activity is a
 *  per-location view (PRT-10) and an unscoped list would mix lobbies. */
export const listBadgePrintRecordsQuerySchema = pageQuerySchema.extend({
    locationId: z.string().min(1),
});
//# sourceMappingURL=badge-print-record.js.map